import type {
  VisibilityCondition,
  LeafCondition,
  RelativeDateValue,
  DynamicDateValue,
  ElemMatchCondition,
} from "../schema/visibility";
import {
  isLeafCondition,
  isAndCondition,
  isOrCondition,
  isNotCondition,
  isElemMatchCondition,
  isComparisonOperator,
} from "../schema/visibility";
import type { VisibilityContext } from "./types";
import { getValueByPath } from "@tellescope/utils";

/**
 * Returns the start of the current day (midnight) as a Unix timestamp.
 */
function startOfDay(timestamp: number): number {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Evaluates a visibility condition against the provided context.
 * Returns true if the block should be visible.
 */
export function evaluateVisibility(condition: VisibilityCondition, context: VisibilityContext): boolean {
  // Handle logical operators (recursive)
  if (isAndCondition(condition)) {
    // Empty $and is vacuously true
    if (condition.$and.length === 0) return true;
    return condition.$and.every((c) => evaluateVisibility(c, context));
  }
  if (isOrCondition(condition)) {
    // Empty $or is vacuously false
    if (condition.$or.length === 0) return false;
    return condition.$or.some((c) => evaluateVisibility(c, context));
  }
  if (isNotCondition(condition)) {
    return !evaluateVisibility(condition.$not, context);
  }

  // Handle $elemMatch: true if at least one item satisfies ALL conditions simultaneously
  if (isElemMatchCondition(condition)) {
    return evaluateElemMatchCondition(condition.$elemMatch, context);
  }

  // Handle leaf condition (has source + field)
  if (isLeafCondition(condition)) {
    return evaluateLeafCondition(condition, context);
  }

  // Unknown structure: default visible
  return true;
}

/**
 * Evaluates a single leaf condition against the context.
 * For array-based sources (formResponse), uses "any match" semantics -
 * condition is true if ANY item in the array matches.
 */
function evaluateLeafCondition(leaf: LeafCondition, context: VisibilityContext): boolean {
  // Get data based on source type
  const sourceData = getSourceData(leaf.source, context);
  if (sourceData === undefined) return false;

  // Array-based sources: use "any match" semantics
  if (Array.isArray(sourceData)) {
    if (sourceData.length === 0) {
      // Handle $exists: false for empty arrays
      if (typeof leaf.value === "object" && leaf.value !== null && "$exists" in leaf.value) {
        return leaf.value.$exists === false;
      }
      return false;
    }
    // Check if ANY item in array matches the condition
    return sourceData.some((item) => {
      const actualValue =
        leaf.field === "$status" ? computeAppointmentStatus(item) : getValueByPath(item, leaf.field).value;
      return evaluateComparison(actualValue, leaf.value);
    });
  }

  // Single-object sources (existing behavior)
  const actualValue =
    leaf.field === "$statuses"
      ? getPatientStatuses(sourceData)
      : leaf.field === "$status"
      ? computeAppointmentStatus(sourceData)
      : getValueByPath(sourceData, leaf.field).value;
  return evaluateComparison(actualValue, leaf.value);
}

/**
 * Evaluates a $elemMatch condition: returns true if at least one item in the source array
 * satisfies ALL field conditions simultaneously (per-item AND semantics).
 */
function evaluateElemMatchCondition(elemMatch: ElemMatchCondition["$elemMatch"], context: VisibilityContext): boolean {
  const sourceData = getSourceData(elemMatch.source, context);
  if (!Array.isArray(sourceData) || sourceData.length === 0) return false;
  return sourceData.some((item) =>
    elemMatch.conditions.every(({ field, value }) => {
      const actual = field === "$status" ? computeAppointmentStatus(item) : getValueByPath(item, field).value;
      return evaluateComparison(actual, value);
    })
  );
}

/**
 * Computes a status string for an appointment/CalendarEvent item.
 * Maps to the EventStatus interface keys: "upcoming", "past", "cancelled", "rescheduled".
 * Priority: cancelled > rescheduled > upcoming > past
 */
function computeAppointmentStatus(item: unknown): string | undefined {
  if (typeof item !== "object" || item === null) return undefined;
  const event = item as Record<string, unknown>;
  if (event.cancelledAt) return "cancelled";
  if (event.rescheduledAt) return "rescheduled";
  const startTime = typeof event.startTimeInMS === "number" ? event.startTimeInMS : undefined;
  if (startTime === undefined) return undefined;
  return startTime > Date.now() ? "upcoming" : "past";
}

/**
 * Extracts patient journey statuses from an enduser object.
 * Returns Object.values(enduser.journeys) as a string array.
 */
function getPatientStatuses(data: unknown): string[] {
  if (typeof data !== "object" || data === null) return [];
  const enduser = data as Record<string, unknown>;
  const journeys = enduser.journeys;
  if (typeof journeys !== "object" || journeys === null) return [];
  return Object.values(journeys).filter((v): v is string => typeof v === "string");
}

/**
 * Gets the data object for a given source type from the context.
 *
 * Most sources have two versions in context:
 * - Singular (e.g. `appointment`): set during item filtering, evaluated as a single object
 * - Plural (e.g. `appointments`): set for block visibility, evaluated with "any match" semantics
 *
 * The singular takes precedence when present. `formResponse` is the exception — it only has
 * a plural form and always uses "any match" evaluation.
 */
function getSourceData(source: string, context: VisibilityContext): unknown {
  switch (source) {
    case "enduser":
      return context.enduser;
    case "formResponse":
      return context.formResponses;
    case "stripeSubscription":
      return context.stripeSubscription ?? context.stripeSubscriptions;
    case "form":
      return context.form;
    case "appointment":
      return context.appointment ?? context.appointments;
    case "order":
      return context.order ?? context.orders;
    case "chatRoom":
      return context.chatRoom ?? context.chatRooms;
    case "file":
      return context.file ?? context.files;
    case "content":
      return context.content ?? context.contents;
    case "purchase":
      return context.purchase ?? context.purchases;
    case "appointmentBookingPage":
      return context.appointmentBookingPage ?? context.appointmentBookingPages;
    default:
      return undefined;
  }
}

/**
 * Evaluates a comparison between an actual value and an expected value/operator.
 */
function evaluateComparison(actual: unknown, expected: unknown): boolean {
  // If expected is a primitive (not an object with operator), treat as $eq
  if (typeof expected !== "object" || expected === null) {
    return actual === expected;
  }

  // Handle comparison operators - use type guard instead of cast
  if (!isComparisonOperator(expected)) {
    // Unknown object structure: default to false
    return false;
  }

  if ("$eq" in expected) return actual === expected.$eq;
  if ("$ne" in expected) return actual !== expected.$ne;
  if ("$gt" in expected) return compareNumeric(actual, expected.$gt) > 0;
  if ("$gte" in expected) return compareNumeric(actual, expected.$gte) >= 0;
  if ("$lt" in expected) return compareNumeric(actual, expected.$lt) < 0;
  if ("$lte" in expected) return compareNumeric(actual, expected.$lte) <= 0;
  if ("$in" in expected) return checkIn(actual, expected.$in);
  if ("$nin" in expected) return !checkIn(actual, expected.$nin);
  if ("$exists" in expected) return checkExists(actual) === expected.$exists;
  if ("$regex" in expected) return checkRegex(actual, expected.$regex);

  // Unknown operator: default to false
  return false;
}

/**
 * Type guard for relative date values.
 */
function isRelativeDateValue(value: unknown): value is RelativeDateValue {
  return typeof value === "object" && value !== null && "$relative" in value;
}

/**
 * Calculates a timestamp from a relative date expression.
 * e.g., { amount: 30, unit: "days", direction: "ago" } => timestamp 30 days ago
 */
function calculateRelativeDate(relative: RelativeDateValue["$relative"]): number {
  const now = Date.now();
  const { amount, unit } = relative;

  switch (unit) {
    case "days":
      return now - amount * 24 * 60 * 60 * 1000;
    case "weeks":
      return now - amount * 7 * 24 * 60 * 60 * 1000;
    case "months": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - amount);
      return d.getTime();
    }
    case "years": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - amount);
      return d.getTime();
    }
    default:
      return now;
  }
}

/**
 * Compares a value numerically against an expected number or dynamic value.
 * Returns: positive if actual > expected, negative if actual < expected, 0 if equal.
 * Supports $now, $today, and $relative dynamic date values.
 */
function compareNumeric(actual: unknown, expected: number | DynamicDateValue): number {
  const now = Date.now();

  // Resolve expected value
  let expectedNum: number;
  if (typeof expected === "number") {
    expectedNum = expected;
  } else if (expected === "$now") {
    expectedNum = now;
  } else if (expected === "$today") {
    expectedNum = startOfDay(now);
  } else if (isRelativeDateValue(expected)) {
    expectedNum = calculateRelativeDate(expected.$relative);
  } else {
    expectedNum = now; // fallback
  }

  // Handle different actual value types
  let actualNum: number;
  if (typeof actual === "number") {
    actualNum = actual;
  } else if (typeof actual === "string") {
    // Try to parse as date string (ISO format)
    const parsed = new Date(actual).getTime();
    actualNum = isNaN(parsed) ? NaN : parsed;
  } else if (actual instanceof Date) {
    // Handle Date objects directly
    actualNum = actual.getTime();
  } else {
    actualNum = NaN;
  }

  if (isNaN(actualNum)) return NaN;
  return actualNum - expectedNum;
}

/**
 * Checks if actual value is in the expected array, OR if actual is an array,
 * checks if any element of actual is in expected (intersection).
 * This provides MongoDB-like $in behavior for both scalar and array fields.
 */
function checkIn(actual: unknown, expected: (string | number | boolean | null)[]): boolean {
  // If actual is an array, check if any element is in expected (intersection)
  if (Array.isArray(actual)) {
    return actual.some((item) => expected.includes(item));
  }
  // Otherwise, check if actual is in expected
  return expected.some((v) => v === actual);
}

/**
 * Checks if a value "exists" (is not null, undefined, empty string, or empty array).
 * Note: 0 and false are considered to "exist".
 */
function checkExists(actual: unknown): boolean {
  if (Array.isArray(actual)) return actual.length > 0;
  return actual !== null && actual !== undefined && actual !== "";
}

/**
 * Checks if actual string matches the expected regex pattern.
 */
function checkRegex(actual: unknown, pattern: string): boolean {
  if (typeof actual !== "string") return false;
  try {
    const regex = new RegExp(pattern);
    return regex.test(actual);
  } catch {
    // Invalid regex pattern
    return false;
  }
}
